import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { exportToSvg } from "@excalidraw/excalidraw";
import { brandConfig, getColor } from "../brandConfig";

interface ExcalidrawViewerProps {
  filePath: string;
  title: string;
  subtitle?: string;
}

const ExcalidrawViewer: React.FC<ExcalidrawViewerProps> = ({
  filePath,
  title,
  subtitle,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(filePath)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
        return res.json();
      })
      .then(async (data) => {
        const svg = await exportToSvg({
          elements: data.elements,
          appState: {
            ...data.appState,
            exportWithDarkMode: false,
            exportBackground: false,
          },
          files: data.files || {},
        });

        svg.removeAttribute("width");
        svg.removeAttribute("height");
        svg.style.width = "100%";
        svg.style.height = "auto";

        if (svgContainerRef.current) {
          svgContainerRef.current.innerHTML = "";
          svgContainerRef.current.appendChild(svg);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [filePath]);

  const textPrimary = getColor("text.primary");

  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={4} align="stretch">
        <Heading size="lg" color={textPrimary}>
          {title}
        </Heading>
        {subtitle && (
          <Text color="gray.500" fontSize="sm">
            {subtitle}
          </Text>
        )}

        {loading && (
          <Center h="300px">
            <Spinner size="xl" color={brandConfig.colors.primary} />
          </Center>
        )}

        {error && (
          <Box
            p={4}
            bg="red.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="red.500"
          >
            <Text color="red.700">Error: {error}</Text>
          </Box>
        )}

        <Box
          ref={svgContainerRef}
          p={4}
          sx={{
            "& svg": {
              width: "100%",
              height: "auto",
              maxWidth: "800px",
              margin: "0 auto",
              display: "block",
            },
          }}
        />
      </VStack>
    </Container>
  );
};

export default ExcalidrawViewer;
